import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import "./Notification.css";
import Navbar from "./Navbar";

const BASE_URL =
  window.location.hostname === "localhost"
  ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const socket = io(BASE_URL, { transports: ["websocket", "polling"] });

function getTypeMeta(n) {
  if (n.type === "message")
    return { icon: "💬", color: "#34d399", bg: "rgba(52,211,153,0.1)", border: "rgba(52,211,153,0.22)" };
  // Admin announcements get a megaphone + amber accent.
  if (n.type === "announcement")
    return { icon: "📢", color: "#fbbf24", bg: "rgba(251,191,36,0.1)", border: "rgba(251,191,36,0.22)" };
  // Refer & Earn reward decisions get a gift + violet accent.
  if (n.type === "referral")
    return { icon: "🎁", color: "#a78bfa", bg: "rgba(167,139,250,0.1)", border: "rgba(167,139,250,0.22)" };
  const msg = (n.message || n.type || "").toLowerCase();
  if (msg.includes("like")) return { icon: "♥", color: "#f472b6", bg: "rgba(244,114,182,0.1)", border: "rgba(244,114,182,0.22)" };
  if (msg.includes("comment")) return { icon: "💬", color: "#a78bfa", bg: "rgba(167,139,250,0.1)", border: "rgba(167,139,250,0.22)" };
  if (msg.includes("follow")) return { icon: "✦", color: "#00ffc8", bg: "rgba(0,255,200,0.1)", border: "rgba(0,255,200,0.22)" };
  if (msg.includes("video") || msg.includes("image") || msg.includes("status"))
    return { icon: "📸", color: "#60a5fa", bg: "rgba(96,165,250,0.1)", border: "rgba(96,165,250,0.22)" };
  return { icon: "🔔", color: "#fbbf24", bg: "rgba(251,191,36,0.1)", border: "rgba(251,191,36,0.22)" };
}

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [clearing, setClearing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  const animRef = useRef(null);
  const notificationsPerPage = 6;
  const userId = localStorage.getItem("userId");
  const navigate = useNavigate();

  const getBlogId = (n) => {
    const b = n.blogId;
    if (!b) return null;
    return typeof b === "object"? b._id : b;
  };

  const getMessageSenderId = (n) => {
    if (n.type!== "message") return null;
    const v = n.viewerId;
    if (!v) return null;
    return typeof v === "object"? v._id : v;
  };

  // NEW: Announcement helpers
  const getAnnouncementId = (n) => {
    const a = n.announcementId;
    if (!a) return null;
    return typeof a === "object"? a._id : a;
  };

  const getAnnouncementLink = (n) => {
    return n.link || n.announcementId?.link || null;
  };

  const isClickable = (n) =>
    Boolean(getBlogId(n) || getMessageSenderId(n) || getAnnouncementId(n) || n.type === "referral");

  const openNotification = (n) => {
    if (n._id) {
      fetch(`${BASE_URL}/api/notifications/read/${n._id}`, { method: "PUT" })
       .then(() => window.dispatchEvent(new Event("notifications-read")))
       .catch(() => {});
    }

    // Refer & Earn: a reward decision always opens the Refer & Earn dashboard,
    // which is where the wallet balance it refers to lives. Handled before the
    // announcement branch because both types carry a `link`.
    if (n.type === "referral") {
      navigate(n.link || "/refer");
      return;
    }

    // Announcement: open its link if it has one, otherwise the detail view.
    if (n.type === "announcement") {
      const link = getAnnouncementLink(n);
      if (link) {
        window.open(link, "_blank", "noopener,noreferrer");
        return;
      }
      const announcementId = getAnnouncementId(n);
      if (announcementId) navigate(`/announcement/${announcementId}`);
      return;
    }

    const senderId = getMessageSenderId(n);
    if (senderId) {
      navigate(`/chat?user=${senderId}`);
      return;
    }
    const blogId = getBlogId(n);
    if (blogId) navigate(`/blog/${blogId}`);
  };

  const COLORS = ["#6332ff", "#00ffc8", "#f472b6", "#60a5fa", "#a78bfa"];
  const makeParticle = useCallback((W, H) => ({
    x: Math.random() * W,
    y: Math.random() * H,
    vx: (Math.random() - 0.5) * 0.38,
    vy: (Math.random() - 0.5) * 0.38,
    r: Math.random() * 1.6 + 0.4,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    alpha: Math.random() * 0.55 + 0.2,
    pulse: Math.random() * Math.PI * 2,
  }), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const root = rootRef.current;
    if (!canvas ||!root) return;
    const ctx = canvas.getContext("2d");
    let W = root.offsetWidth;
    let H = Math.max(root.offsetHeight, window.innerHeight);
    let particles = [];
    const resize = () => {
      W = root.offsetWidth;
      H = Math.max(root.offsetHeight, window.innerHeight);
      canvas.width = W; canvas.height = H;
    };
    resize();
    particles = Array.from({ length: 75 }, () => makeParticle(W, H));
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy; p.pulse += 0.018;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        const pa = p.alpha * (0.7 + 0.3 * Math.sin(p.pulse));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = pa;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = particles[i].color;
            ctx.lineWidth = 0.5;
            ctx.globalAlpha = (1 - dist / 100) * 0.1;
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    const ro = new ResizeObserver(resize);
    ro.observe(root);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [makeParticle]);

  const getNotifications = async () => {
    if (!userId) { setLoading(false); return; }
    try {
      const res = await fetch(`${BASE_URL}/api/notifications/${userId}`);
      const data = await res.json();
      setNotifications(Array.isArray(data)? data : []);
    } catch (err) { console.log(err); }
    finally { setLoading(false); }
  };

  const markAllRead = async () => {
    if (!userId) return;
    try {
      await fetch(`${BASE_URL}/api/notifications/${userId}/mark-read`, { method: "PUT" });
      window.dispatchEvent(new Event("notifications-read"));
    } catch (err) { console.log(err); }
  };

  const deleteSingle = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await fetch(`${BASE_URL}/api/notifications/single/${notificationId}`, { method: "DELETE" });
      setNotifications((prev) => prev.filter((n) => n._id!== notificationId));
    } catch (err) { console.log(err); }
  };

  const clearAllNotifications = async () => {
    if (!userId) return;
    setClearing(true);
    try {
      const res = await fetch(`${BASE_URL}/api/notifications/clear/${userId}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        setNotifications([]);
        setNewCount(0);
        setCurrentPage(1);
        setShowConfirm(false);
        window.dispatchEvent(new Event("notifications-read"));
      }
    } catch (err) {
      console.error("Clear notifications error:", err);
    } finally { setClearing(false); }
  };

  useEffect(() => {
    (async () => { await getNotifications(); await markAllRead(); })();
    if (userId) socket.emit("join", userId);
    const handleNew = (data) => {
      const targetId = data.userId || data.receiverId || data.toUserId;
      if (targetId && targetId.toString()!== userId) return;
      setNotifications((prev) => {
        if (prev.find((n) => n._id === data._id)) return prev;
        return [data,...prev];
      });
      setNewCount((c) => c + 1);
      setCurrentPage(1);
      markAllRead();
    };
    socket.on("newNotification", handleNew);
    socket.on("notification", handleNew);
    socket.on("new_notification", handleNew);
    socket.on("statusNotification", handleNew);
    return () => {
      socket.off("newNotification", handleNew);
      socket.off("notification", handleNew);
      socket.off("new_notification", handleNew);
      socket.off("statusNotification", handleNew);
    };
  }, [userId]);

  const clearNewCount = () => setNewCount(0);
  const indexOfLast = currentPage * notificationsPerPage;
  const indexOfFirst = indexOfLast - notificationsPerPage;
  const currentNotifs = notifications.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(notifications.length / notificationsPerPage);

  return (
    <div className="np2-root" ref={rootRef} onClick={clearNewCount}>
      <canvas className="np2-canvas" ref={canvasRef} />
      <div className="np2-aurora" />
      <Navbar />
      <div className="np2-container">
        <div className="np2-header">
          <div className="np2-header-label">✦ activity feed</div>
          <div className="np2-title-row">
            <h1 className="np2-title">Notifications</h1>
            {newCount > 0 && <span className="np2-new-badge">{newCount} new</span>}
          </div>
          <p className="np2-subtitle">
            {notifications.length > 0? `${notifications.length} total notification${notifications.length > 1? "s" : ""}` : "all caught up ✓"}
          </p>
        </div>

        <div className="np2-toolbar">
          <div className="np2-live-row">
            <span className="np2-live-dot" />
            <span className="np2-live-text">Live · updates instantly</span>
          </div>
          {notifications.length > 0 && (
            <button className="np2-clear-btn" onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }} disabled={clearing}>
              {clearing? <span className="np2-clear-spinner" /> : <>🗑️ Clear all</>}
            </button>
          )}
        </div>

        {showConfirm && (
          <div className="np2-confirm-overlay" onClick={(e) => { e.stopPropagation(); setShowConfirm(false); }}>
            <div className="np2-confirm-box" onClick={(e) => e.stopPropagation()}>
              <div className="np2-confirm-icon">🗑️</div>
              <h3 className="np2-confirm-title">Clear all notifications?</h3>
              <p className="np2-confirm-sub">This action cannot be undone.</p>
              <div className="np2-confirm-actions">
                <button className="np2-confirm-cancel" onClick={() => setShowConfirm(false)}>Cancel</button>
                <button className="np2-confirm-yes" onClick={clearAllNotifications} disabled={clearing}>
                  {clearing? "Clearing…" : "Yes, clear all"}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading? (
          <div className="np2-loading"><span className="np2-dot" /><span className="np2-dot" /><span className="np2-dot" /></div>
        ) : notifications.length === 0? (
          <div className="np2-empty"><div className="np2-empty-icon">🔔</div><p>No notifications yet</p></div>
        ) : (
          <>
            <div className="np2-list">
              {currentNotifs.map((n, i) => {
                const meta = getTypeMeta(n);
                const sender = n.viewerId || n.senderId || {};
                const pic = sender.profilePic || "https://cdn-icons-png.flaticon.com/512/149/149071.png";
                const name = sender.name || sender.username || n.senderName || "Admin";
                const mediaItem = n.statusId?.media || n.blogId?.image || null;
                const clickable = isClickable(n);

                return (
                  <div key={n._id || i} className={`np2-card ${clickable? "np2-card-clickable" : ""}`} onClick={clickable? () => openNotification(n) : undefined}
                    style={{ "--c": meta.color, "--bg": meta.bg, "--br": meta.border, animationDelay: `${i * 0.04}s` }}>

                    <button className="np2-delete-btn" onClick={(e) => deleteSingle(n._id, e)}>✕</button>

                    <div className="np2-type-icon" style={{ background: meta.bg, border: `1px solid ${meta.border}`, color: meta.color }}>
                      {meta.icon}
                    </div>

                    <div className="np2-content">
                      <div className="np2-main">
                        <img src={pic} alt="" className="np2-avatar" />
                        <p className="np2-text"><strong>{name}</strong> {n.message}</p>
                        {mediaItem && <img src={mediaItem} alt="" className="np2-media-preview" />}
                      </div>
                      <span className="np2-time">{timeAgo(n.createdAt)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination Buttons */}
            {totalPages > 1 && (
              <div className="np2-pagination">
                <button
                  className="np2-page-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ← Prev
                </button>
                <span className="np2-page-info">Page {currentPage} of {totalPages}</span>
                <button
                  className="np2-page-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Notifications;